import { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Button, Switch, FormControlLabel, Alert, MenuItem, Paper, Chip,
} from '@mui/material';
import { getTiers, upsertTier } from '../../api/tierApi';

const PP = "'Poppins', sans-serif";
const COLOR_OPTS = ['slate', 'bronze', 'silver', 'gold', 'diamond'];

const BADGE_BG = {
  slate: '#F1F5F9', bronze: '#FEF3C7', silver: '#E5E7EB', gold: '#FEF3C7', diamond: '#DBEAFE',
};
const BADGE_FG = {
  slate: '#475569', bronze: '#92400E', silver: '#374151', gold: '#B45309', diamond: '#1D4ED8',
};

export default function TierSettings() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const load = () =>
    getTiers().then(r => setRows(r.data || [])).catch(e => setError(e.response?.data ?? e.message));

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    tierId: null,
    name: '',
    sortOrder: rows.length + 1,
    revenueThreshold: 0,
    pscPercent: 5,
    docPercent: 1,
    tocPercent: 5,
    badgeColor: 'slate',
    isActive: true,
  });

  const save = async () => {
    setError('');
    try {
      await upsertTier(editing);
      setEditing(null);
      load();
    } catch (e) {
      setError(e.response?.data ?? e.message);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h6" sx={{ fontFamily: PP, fontWeight: 700, mb: 0.5 }}>
        Tier Settings
      </Typography>
      <Typography sx={{ fontFamily: PP, color: '#64748B', fontSize: '0.85rem', mb: 2 }}>
        Tier names are used as the agent's professional title (e.g. on namecards). PSC% is the rate granted on promotion.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>}
      <Button variant="contained" onClick={startNew} sx={{ mb: 2, textTransform: 'none' }}>
        + New Tier
      </Button>
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Title (Tier Name)</TableCell>
              <TableCell>Revenue Threshold (RM)</TableCell>
              <TableCell>PSC %</TableCell>
              <TableCell>DOC %</TableCell>
              <TableCell>TOC %</TableCell>
              <TableCell>Badge</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>—</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(t => (
              <TableRow key={t.tierId} hover>
                <TableCell>{t.sortOrder}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Chip
                    label={t.name}
                    size="small"
                    sx={{
                      backgroundColor: BADGE_BG[t.badgeColor] ?? BADGE_BG.slate,
                      color: BADGE_FG[t.badgeColor] ?? BADGE_FG.slate,
                      fontWeight: 700,
                      fontFamily: PP,
                    }}
                  />
                </TableCell>
                <TableCell>{Number(t.revenueThreshold).toLocaleString()}</TableCell>
                <TableCell>{t.pscPercent}%</TableCell>
                <TableCell>{t.docPercent}%</TableCell>
                <TableCell>{t.tocPercent}%</TableCell>
                <TableCell>{t.badgeColor}</TableCell>
                <TableCell>{t.isActive ? '✓' : '—'}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => setEditing({ ...t })}>Edit</Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ color: '#64748B', textAlign: 'center', py: 3 }}>
                  No tiers configured.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {editing && (
        <Paper sx={{ mt: 3, p: 2, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
          <Typography sx={{ fontFamily: PP, fontWeight: 600, mb: 2 }}>
            {editing.tierId ? `Edit "${editing.name}"` : 'New Tier'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField
              label="Title (Tier Name)"
              size="small"
              value={editing.name}
              onChange={e => setEditing({ ...editing, name: e.target.value })}
              helperText="e.g. Junior Sales / Senior Sales / Sales Lead"
            />
            <TextField
              label="Sort order"
              size="small"
              type="number"
              value={editing.sortOrder}
              onChange={e => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
            />
            <TextField
              label="Revenue Threshold (RM)"
              size="small"
              type="number"
              value={editing.revenueThreshold}
              onChange={e => setEditing({ ...editing, revenueThreshold: Number(e.target.value) })}
              helperText="FinalPrice only since 1 May 2026; cobroke = half-credit"
            />
            <TextField
              label="PSC %"
              size="small"
              type="number"
              value={editing.pscPercent}
              onChange={e => setEditing({ ...editing, pscPercent: Number(e.target.value) })}
            />
            <TextField
              label="DOC %"
              size="small"
              type="number"
              value={editing.docPercent ?? 0}
              onChange={e => setEditing({ ...editing, docPercent: Number(e.target.value) })}
            />
            <TextField
              label="TOC %"
              size="small"
              type="number"
              value={editing.tocPercent ?? 0}
              onChange={e => setEditing({ ...editing, tocPercent: Number(e.target.value) })}
            />
            <TextField
              select
              label="Badge color"
              size="small"
              value={editing.badgeColor}
              onChange={e => setEditing({ ...editing, badgeColor: e.target.value })}
            >
              {COLOR_OPTS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Switch checked={editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} />}
              label="Active"
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!editing.name?.trim()}>Save</Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
